import React, { useEffect, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
} from '@mui/material';
import { Campaign } from '@internal/backstage-plugin-bidone-backend';
import { useUpdateCampaign } from '../../hooks/useCampaigns';
import { OwnerOption, useOwnerOptions } from './CreateCampaignDialog';

type EditCampaignDialogProps = {
  open: boolean;
  campaign: Campaign;
  onClose: () => void;
};

// Edit the campaign's details: name, owner, and the start/target dates the
// derived state (scheduled/in progress/delayed) is computed from.
export const EditCampaignDialog = ({
  open,
  campaign,
  onClose,
}: EditCampaignDialogProps) => {
  const { data: ownerOptions } = useOwnerOptions();
  const updateCampaign = useUpdateCampaign();

  const [name, setName] = useState('');
  const [owner, setOwner] = useState<OwnerOption | null>(null);
  const [startDate, setStartDate] = useState('');
  const [targetDate, setTargetDate] = useState('');
  const [membershipMode, setMembershipMode] = useState<'live' | 'snapshot'>(
    'live',
  );

  useEffect(() => {
    if (open) {
      setName(campaign.name);
      setMembershipMode(campaign.membershipMode);
      setOwner(
        campaign.ownerEntityRef
          ? {
              ref: campaign.ownerEntityRef,
              label:
                (ownerOptions ?? []).find(
                  o => o.ref === campaign.ownerEntityRef,
                )?.label ?? campaign.ownerEntityRef,
            }
          : null,
      );
      setStartDate(campaign.startDate?.slice(0, 10) ?? '');
      setTargetDate(campaign.targetDate?.slice(0, 10) ?? '');
      updateCampaign.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, campaign]);

  const handleSave = async () => {
    await updateCampaign.mutateAsync({
      id: campaign.id,
      patch: {
        name: name.trim(),
        ownerEntityRef: owner?.ref ?? campaign.ownerEntityRef,
        startDate: startDate || null,
        targetDate: targetDate || null,
        membershipMode,
      },
    });
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit campaign details</DialogTitle>
      <DialogContent>
        {updateCampaign.error ? (
          <Alert severity="error">
            {(updateCampaign.error as Error).message}
          </Alert>
        ) : null}
        <TextField
          sx={{ mb: 2 }}
          label="Campaign name"
          variant="outlined"
          size="small"
          fullWidth
          value={name}
          onChange={e => setName(e.target.value)}
        />
        <Autocomplete
          sx={{ mb: 2 }}
          size="small"
          options={ownerOptions ?? []}
          getOptionLabel={o => o.label}
          isOptionEqualToValue={(a, b) => a.ref === b.ref}
          value={owner}
          onChange={(_e, next) => setOwner(next)}
          renderInput={params => (
            <TextField {...params} label="Owner" variant="outlined" />
          )}
        />
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            label="Start date"
            type="date"
            variant="outlined"
            size="small"
            fullWidth
            InputLabelProps={{ shrink: true }}
            value={startDate}
            onChange={e => setStartDate(e.target.value)}
          />
          <TextField
            label="Target date"
            type="date"
            variant="outlined"
            size="small"
            fullWidth
            InputLabelProps={{ shrink: true }}
            value={targetDate}
            onChange={e => setTargetDate(e.target.value)}
          />
        </Box>
        <TextField
          sx={{ mt: 2 }}
          label="Membership"
          variant="outlined"
          size="small"
          select
          fullWidth
          value={membershipMode}
          onChange={e =>
            setMembershipMode(e.target.value as 'live' | 'snapshot')
          }
          helperText={
            membershipMode === 'snapshot'
              ? 'Snapshot — membership is a frozen, hand-curated set. Curate it from the campaign page.'
              : 'Live — membership re-evaluates from the filter (new matches auto-join).'
          }
        >
          <MenuItem value="live">Live (follow filter)</MenuItem>
          <MenuItem value="snapshot">Snapshot (frozen cohort)</MenuItem>
        </TextField>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          color="primary"
          variant="contained"
          disabled={!name.trim() || updateCampaign.isPending}
          onClick={() => {
            handleSave().catch(() => undefined);
          }}
        >
          {updateCampaign.isPending ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
